import { useCallback, useEffect, useState } from 'react';
import { InvestorsTableRow } from './InvestorsTableRow';
import { Pagination } from '@/components/ui/pagination';
import type {
  InvestorDetail,
  InvestorSummary,
  InvestorWithFundContext,
} from '@/types/investor';
import { usePrefetchInvestor } from '@/hooks/react-query/usePrefetch';
import { useNavigate } from 'react-router';

interface PaginationData {
  page: number;
  total: number;
  page_size: number;
  total_pages: number;
}

interface InvestorsTableProps {
  investors: (InvestorSummary | InvestorDetail)[];
  fundId?: string;
  pagination?: PaginationData;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const InvestorsTable = ({
  investors,
  fundId,
  pagination,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: InvestorsTableProps) => {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
  const prefetchInvestor = usePrefetchInvestor();
  const navigate = useNavigate();

  const handleRowClick = useCallback(
    (investor: InvestorSummary | InvestorDetail | InvestorWithFundContext) => {
      navigate(`/app/investors/${investor.id}`);
    },
    [navigate]
  );

  const handleRowHover = useCallback(
    (investorId: string) => {
      prefetchInvestor(investorId);
    },
    [prefetchInvestor]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      onPageChange(newPage);
      setSelectedRowIndex(-1);
    },
    [onPageChange]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      onPageSizeChange(newPageSize);
      setSelectedRowIndex(-1);
    },
    [onPageSizeChange]
  );

  // Keyboard navigation handlers
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.getAttribute('contenteditable') === 'true');

      switch (event.key) {
        case 'ArrowUp':
          // Allow arrow navigation even when search is focused, but not other inputs
          if (!isInputFocused || activeElement?.id === 'investor-search') {
            event.preventDefault();
            setSelectedRowIndex((prev) => {
              const newIndex = prev <= 0 ? investors.length - 1 : prev - 1;
              return investors.length > 0 ? newIndex : -1;
            });
          }
          break;

        case 'ArrowDown':
          // Allow arrow navigation even when search is focused, but not other inputs
          if (!isInputFocused || activeElement?.id === 'investor-search') {
            event.preventDefault();
            setSelectedRowIndex((prev) => {
              const newIndex = prev >= investors.length - 1 ? 0 : prev + 1;
              return investors.length > 0 ? newIndex : -1;
            });
          }
          break;

        case 'Enter':
          // Only navigate if search is not focused or if a row is selected
          if (
            (!isInputFocused || activeElement?.id === 'investor-search') &&
            selectedRowIndex >= 0 &&
            selectedRowIndex < investors.length
          ) {
            event.preventDefault();
            const selectedInvestor = investors[selectedRowIndex];
            navigate(`/app/investors/${selectedInvestor.id}`);
          }
          break;

        case 'Escape':
          event.preventDefault();
          setSelectedRowIndex(-1);
          break;
      }
    },
    [investors, selectedRowIndex, navigate]
  );

  // Set up keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Entity Type
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Funds
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider text-right">
                {fundId ? 'Commitment' : 'Total Commitments'}
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Contacts
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Last Active in Portal
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {investors.map((investor, index) => (
              <InvestorsTableRow
                key={investor.id}
                investor={investor}
                fundId={fundId}
                isSelected={selectedRowIndex === index}
                onClick={handleRowClick}
                onHover={handleRowHover}
              />
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.total > 0 && (
        <div className="p-4">
          <Pagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pageSize}
            onChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showSizeChanger
            pageSizeOptions={[5, 25, 50, 100]}
          />
        </div>
      )}
    </div>
  );
};
